// src/pages/Login.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import logoImage from "@/assets/DISLOGIQ - LOGO.svg";
import backgroundImage from "@/assets/DISLOG-BG.png";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";
const DEFAULT_PASSWORD = "DISLOG2026";

function normalizeLogin(input: string) {
  return input.trim().toLowerCase();
}

const Login = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usernameOrEmail || !password) return;

    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          username: normalizeLogin(usernameOrEmail),
          password,
        }),
      });

      const data: any = await res.json().catch(() => null);
      if (!res.ok || !data) {
        throw new Error(data?.detail || "Identifiants invalides");
      }

      // Token + user
      localStorage.setItem("auth_token", data.access_token);
      localStorage.setItem("auth_user", JSON.stringify(data.user || {}));

      const usedDefault = password.trim().toLowerCase() === DEFAULT_PASSWORD.toLowerCase();
      const userMustChange =
        !!data.user?.must_change_password || !!data.user?.mustChangePassword || false;
      const mustChange = userMustChange || usedDefault;
      const mustVerify = !!data?.mustVerifyCode || usedDefault;

      // Contexte
      login({
        email: data.user?.email,
        name: data.user?.name,
        role: data.user?.role,
        mustChangePassword: mustChange,
      });
      localStorage.setItem("must_verify_code", mustVerify ? "1" : "0");

      toast({
        title: "Connexion réussie",
        description: `Bienvenue, ${data.user?.name} (${data.user?.role})`,
      });

      if (mustChange && mustVerify) navigate("/verify-initial-code");
      else if (mustChange) navigate("/first-login-change-password");
      else navigate("/dashboard");
    } catch (error: any) {
      toast({
        title: "Échec de la connexion",
        description: error?.message ?? "Vérifiez vos identifiants.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Background flou */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(10px)",
          transform: "scale(1.08)",
        }}
      />
      {/* Overlay lisibilité */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />

      {/* Wrapper carte + logo */}
      <div className="relative w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-300 pt-10">
        {/* Logo au-dessus de la carte */}
        <div className="flex justify-center mb-6 animate-in fade-in-0 slide-in-from-top-1 duration-500">
          <img 
            src={logoImage} 
            alt="DISLOGIQ" 
            className="h-8 w-auto"
          />
        </div>

        {/* Carte */}
        <div className="relative z-10 rounded-3xl shadow-2xl backdrop-blur-xl border border-white/20 bg-white/30 dark:bg-black/30 animate-in fade-in-0 zoom-in-95 duration-500">
          <div className="p-6 sm:p-7">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold tracking-tight text-foreground animate-in fade-in-0 slide-in-from-top-2 duration-500">
                Bienvenue
              </h1>
              <p className="text-muted-foreground/70 text-xs mt-1 animate-in fade-in-0 slide-in-from-top-3 duration-500 delay-75">
                Automatisation intelligente de vos rapports quotidiens
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5 animate-in fade-in-0 slide-in-from-bottom-4 duration-500 delay-150">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nom@exemple.com"
                  value={usernameOrEmail}
                  onChange={(e) => setUsernameOrEmail(e.target.value)}
                  required
                  autoComplete="username"
                  autoFocus
                  className="bg-background/60"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="bg-background/60"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 font-semibold hover:shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                disabled={isLoading || !usernameOrEmail || !password}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connexion...
                  </span>
                ) : (
                  "Se connecter"
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-primary text-sm hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
